import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import { devices_config } from '@devices/index';
import type { Room } from 'src/types/Room';
import { devices } from 'src/stores/lists/devices';
import { rooms } from 'src/stores/lists/rooms';
import type { DashWidgetEditType } from './dash-types';
import {
	DASH_ENERGY_INFO_CATEGORY_ORDER,
	isDashEnergyWidgetType,
	type DashEnergyHeatmapWidgetConfig,
	type DashEnergyInfoKpiId,
	type DashEnergyWidgetConfig,
	type DashEnergyWidgetConfigMap,
	type DashEnergyWidgetTypes,
} from './dash-energy-types';
import {
	LEGACY_DASH_ENERGY_INFO_KPI_IDS,
	LEGACY_DASH_ENERGY_INFO_PERIODS,
	isLegacyDashEnergyInfoWidgetType,
	type LegacyDashEnergyInfoWidgetType,
} from './dash-energy-legacy';
import type { EnergyHeatmapMetric } from 'src/components/EnergyMonitoring/helpers/energy_heatmap_helpers';
import { getEnergyWidgetDefinition } from './dash-energy-registry';

export const normalizeHeatmapMetric = (
	config?: Pick<Partial<DashEnergyHeatmapWidgetConfig>, 'heatmapMetric'> | null,
): EnergyHeatmapMetric => {
	if (config?.heatmapMetric === 'consumption' || config?.heatmapMetric === 'generation') {
		return config.heatmapMetric;
	}
	return 'consumption';
};

export const normalizeInfoCategories = (
	categories?: Array<DashEnergyInfoKpiId | 'battery'> | null,
): DashEnergyInfoKpiId[] => {
	if (!categories?.length) return ['load'];
	const selected = new Set(
		categories.map((id): DashEnergyInfoKpiId => (id === 'battery' ? 'selfPowered' : id)),
	);
	return DASH_ENERGY_INFO_CATEGORY_ORDER.filter((id) => selected.has(id));
};

export const selectItemsToInfoCategories = (
	selectItems: DashWidgetEditType['selectItems'] = [],
): DashEnergyInfoKpiId[] =>
	normalizeInfoCategories(
		[...selectItems]
			.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
			.map((item) => String(item.id) as DashEnergyInfoKpiId),
	);

export const infoCategoriesToSelectItems = (
	categories: DashEnergyInfoKpiId[],
): NonNullable<DashWidgetEditType['selectItems']> =>
	normalizeInfoCategories(categories).map((id, position) => ({
		type: 'dashEnergyInfo',
		itemType: 'energy_info',
		id,
		position,
	}));

export const getDefaultEnergyConfigForType = <T extends DashEnergyWidgetTypes>(
	type: T,
): DashEnergyWidgetConfig<T> =>
	({
		showPeriodLabel: true,
		...getEnergyWidgetDefinition(type).defaultConfig,
	}) as DashEnergyWidgetConfig<T>;

/**
 * Reads a flat persisted config using its parent widget type and applies only that widget's
 * defaults. Extra legacy keys are retained at runtime so editing old dashboard JSON is lossless.
 */
export const normalizeEnergyConfig = <T extends DashEnergyWidgetTypes>(
	type: T,
	config?: Partial<DashEnergyWidgetConfig> | Partial<DashEnergyWidgetConfigMap[T]> | null,
): DashEnergyWidgetConfig<T> => {
	const normalized = {
		...getDefaultEnergyConfigForType(type),
		...config,
	} as DashEnergyWidgetConfig<T>;
	const definition = getEnergyWidgetDefinition(type);
	if (
		definition.supportsPeriod &&
		normalized.period === 'live' &&
		definition.defaultConfig.period !== 'live'
	) {
		normalized.period = definition.defaultConfig.period;
	}

	if (type === 'dashEnergyInfo') {
		const infoConfig = normalized as DashEnergyWidgetConfig<'dashEnergyInfo'>;
		infoConfig.infoCategories = normalizeInfoCategories(infoConfig.infoCategories);
		if (
			infoConfig.period === 'live' &&
			infoConfig.infoCategories.some((id) =>
				['periodConsumption', 'periodGeneration', 'tariff', 'offPeak', 'peakDemand'].includes(
					id,
				),
			)
		) {
			infoConfig.period = '24_hours';
		}
	}

	if (type === 'dashEnergyHeatmap') {
		const heatmapConfig = normalized as DashEnergyWidgetConfig<'dashEnergyHeatmap'>;
		heatmapConfig.heatmapScope = ['all', 'room', 'device'].includes(heatmapConfig.heatmapScope)
			? heatmapConfig.heatmapScope
			: 'all';
		if (heatmapConfig.heatmapScope !== 'room') heatmapConfig.roomId = undefined;
		if (heatmapConfig.heatmapScope !== 'device') heatmapConfig.deviceId = undefined;
		heatmapConfig.heatmapMetric = normalizeHeatmapMetric(heatmapConfig);
		heatmapConfig.heatmapVariant = heatmapConfig.heatmapVariant === 'minimal' ? 'minimal' : 'full';
		heatmapConfig.allowInWidgetMetricToggle = heatmapConfig.allowInWidgetMetricToggle === true;
	}

	if (type === 'dashEnergyPeak') {
		const peakConfig = normalized as DashEnergyWidgetConfig<'dashEnergyPeak'>;
		peakConfig.peakVariant = ['overview', 'byMeter', 'phaseBalance'].includes(peakConfig.peakVariant)
			? peakConfig.peakVariant
			: 'overview';
	}

	if (type === 'dashEnergyTopConsumers') {
		const topConsumersConfig = normalized as DashEnergyWidgetConfig<'dashEnergyTopConsumers'>;
		topConsumersConfig.limit = 5;
	}

	if (type === 'dashEnergyFlow') {
		const flowConfig = normalized as DashEnergyWidgetConfig<'dashEnergyFlow'> & {
			showGeneration?: boolean;
		};
		const sourceConfig = config as
			| { showSupply?: boolean; showGeneration?: boolean }
			| null
			| undefined;
		if (sourceConfig?.showSupply === undefined && sourceConfig?.showGeneration !== undefined) {
			flowConfig.showSupply = sourceConfig.showGeneration;
		}
		flowConfig.supplyMode = ['auto', 'measured', 'calculated'].includes(flowConfig.supplyMode)
			? flowConfig.supplyMode
			: 'auto';
	}

	return normalized;
};

/** Typed accessor for configs held at untyped persistence or component boundaries. */
export const getEnergyConfigForType = <T extends DashEnergyWidgetTypes>(
	type: T,
	config?: Partial<DashEnergyWidgetConfig> | null,
): DashEnergyWidgetConfig<T> => normalizeEnergyConfig(type, config);

export const getRoomsWithPmDevices = () => {
	const roomIdsWithPmDevices = new Set(
		Object.values(get(devices))
			.filter((device) => devices_config[device.type]?.isPM)
			.map((device) => Number(device.room_id)),
	);

	return (Object.values(get(rooms)) as Room[]).filter((room) =>
		roomIdsWithPmDevices.has(Number(room.id)),
	);
};

export const buildEnergyWidgetCardRef = (
	type: DashEnergyWidgetTypes,
	refId: string | number,
	position = 0,
) => ({
	id: refId,
	_type: 'energy',
	position,
});

export const migrateLegacyEnergyWidget = (
	type: DashEnergyWidgetTypes | LegacyDashEnergyInfoWidgetType,
	config?: Partial<DashEnergyWidgetConfig> | null,
): {
	type: DashEnergyWidgetTypes;
	energyConfig: DashEnergyWidgetConfig;
} => {
	if (!isLegacyDashEnergyInfoWidgetType(type)) {
		return { type, energyConfig: normalizeEnergyConfig(type, config) };
	}

	return {
		type: 'dashEnergyInfo',
		energyConfig: normalizeEnergyConfig('dashEnergyInfo', {
			period: config?.period ?? LEGACY_DASH_ENERGY_INFO_PERIODS[type],
			infoCategories: [LEGACY_DASH_ENERGY_INFO_KPI_IDS[type]],
			showTariffBar: type === 'dashEnergyInfoCost',
		}),
	};
};

export const genEnergyWidgetData = (widget: {
	id?: string;
	title?: string;
	showWidgetTitle?: boolean;
	type?: DashEnergyWidgetTypes | LegacyDashEnergyInfoWidgetType;
	col?: 'left' | 'right';
	position?: number;
	energyConfig?: Partial<DashEnergyWidgetConfig>;
}) => {
	const migrated = migrateLegacyEnergyWidget(
		widget.type as DashEnergyWidgetTypes | LegacyDashEnergyInfoWidgetType,
		widget.energyConfig,
	);
	const type = migrated.type;
	const energyConfig = migrated.energyConfig;
	const roomChartConfig =
		type === 'dashEnergyRoomChart'
			? getEnergyConfigForType('dashEnergyRoomChart', energyConfig)
			: undefined;
	const heatmapConfig =
		type === 'dashEnergyHeatmap'
			? getEnergyConfigForType('dashEnergyHeatmap', energyConfig)
			: undefined;
	const infoConfig =
		type === 'dashEnergyInfo' ? getEnergyConfigForType('dashEnergyInfo', energyConfig) : undefined;
	const refId =
		roomChartConfig?.roomId != null
			? `room-${roomChartConfig.roomId}`
			: type === 'dashEnergyHeatmap' &&
				  heatmapConfig?.heatmapScope === 'room' &&
				  heatmapConfig.roomId != null
				? `heatmap-room-${heatmapConfig.roomId}`
				: type === 'dashEnergyHeatmap' &&
					  heatmapConfig?.heatmapScope === 'device' &&
					  heatmapConfig.deviceId
					? `heatmap-device-${heatmapConfig.deviceId}`
					: type === 'dashEnergyInfo'
						? 'dashEnergyInfo'
						: type;

	const cards = infoConfig
		? normalizeInfoCategories(infoConfig.infoCategories).map((categoryId, position) =>
				buildEnergyWidgetCardRef(type, categoryId, position),
			)
		: [buildEnergyWidgetCardRef(type, refId)];

	return {
		id: widget.id,
		title: widget.title,
		showWidgetTitle: widget.showWidgetTitle ?? false,
		isDndShadowItem: false,
		type,
		col: widget.col,
		position: widget.position,
		energyConfig,
		cards,
	};
};

export const getEnergyWidgetTitleProposal = (type: DashEnergyWidgetTypes): string => {
	const definition = getEnergyWidgetDefinition(type);
	return get(format)(definition.titleKey, { default: definition.titleDefault });
};

const buildSingleEnergyWidgetPayload = (
	editItem: DashWidgetEditType,
	overrides: {
		selectItems?: DashWidgetEditType['selectItems'];
		energyConfig?: DashEnergyWidgetConfig;
	},
): DashWidgetEditType => ({
	...editItem,
	showWidgetTitle: false,
	selectItems: overrides.selectItems ?? editItem.selectItems,
	energyConfig: overrides.energyConfig ?? editItem.energyConfig,
});

/** Fan-out: one standalone widget per selected room (or single widget otherwise). */
export const requestAddDashEnergyWidgets = async (editItem: DashWidgetEditType) => {
	const { requestAddDashItem } = await import('./dash-helpers');

	if (!isDashEnergyWidgetType(editItem.widgetType)) {
		await requestAddDashItem(editItem);
		return;
	}

	const type = editItem.widgetType;
	const baseConfig = normalizeEnergyConfig(
		type,
		editItem.energyConfig ?? getDefaultEnergyConfigForType(type),
	);

	if (type === 'dashEnergyRoomChart' && editItem.selectItems.length > 0 && !editItem.id) {
		for (const item of editItem.selectItems) {
			const roomId = Number(item.id);
			const room = get(rooms)[roomId];
			const roomTitle = room?.name
				? `${get(format)('energy_monitoring_room_chart_title', { default: 'Room' })}: ${room.name}`
				: editItem.widgetTitle;

			await requestAddDashItem({
				...buildSingleEnergyWidgetPayload(editItem, {
					selectItems: [item],
					energyConfig: { ...baseConfig, roomId },
				}),
				id: undefined,
				position: undefined,
				widgetTitle: roomTitle,
			});
		}
		return;
	}

	if (type === 'dashEnergyRoomChart' && editItem.id) {
		const roomChartConfig = getEnergyConfigForType('dashEnergyRoomChart', baseConfig);
		const roomId = Number(editItem.selectItems[0]?.id ?? roomChartConfig.roomId);
		await requestAddDashItem(
			buildSingleEnergyWidgetPayload(editItem, {
				energyConfig: { ...roomChartConfig, roomId },
			}),
		);
		return;
	}

	if (type === 'dashEnergyInfo') {
		const infoCategories = selectItemsToInfoCategories(editItem.selectItems);
		await requestAddDashItem(
			buildSingleEnergyWidgetPayload(editItem, {
				energyConfig: { ...baseConfig, infoCategories },
				selectItems: infoCategoriesToSelectItems(infoCategories),
			}),
		);
		return;
	}

	if (type === 'dashEnergyHeatmap') {
		const heatmapConfig = getEnergyConfigForType('dashEnergyHeatmap', baseConfig);
		const scope = heatmapConfig.heatmapScope;
		let roomId = heatmapConfig.roomId;
		let deviceId = heatmapConfig.deviceId;
		let widgetTitle = editItem.widgetTitle;
		const heatmapTitle =
			heatmapConfig.heatmapVariant === 'minimal'
				? get(format)('dash_energy_heatmap_minimal_title', {
						default: 'Monthly Heatmap',
					})
				: get(format)('dash_energy_heatmap_title', {
						default: 'Yearly Heatmap',
					});

		if (scope === 'room' && editItem.selectItems.length > 0) {
			roomId = Number(editItem.selectItems[0].id);
			const room = get(rooms)[roomId];
			if (room?.name) {
				widgetTitle = `${heatmapTitle}: ${room.name}`;
			}
		}

		if (scope === 'device' && editItem.selectItems.length > 0) {
			deviceId = String(editItem.selectItems[0].id);
			const device = get(devices)[deviceId];
			if (device?.name) {
				widgetTitle = `${heatmapTitle}: ${device.name}`;
			}
		}

		await requestAddDashItem({
			...buildSingleEnergyWidgetPayload(editItem, {
				energyConfig: {
					...heatmapConfig,
					roomId,
					deviceId,
					heatmapScope: scope,
				},
				selectItems: [
					{
						type,
						itemType: 'energy',
						id:
							scope === 'room' && roomId != null
								? `heatmap-room-${roomId}`
								: scope === 'device' && deviceId
									? `heatmap-device-${deviceId}`
									: type,
						position: 0,
					},
				],
			}),
			widgetTitle,
		});
		return;
	}

	await requestAddDashItem(
		buildSingleEnergyWidgetPayload(editItem, {
			energyConfig: baseConfig,
			selectItems: [
				{
					type,
					itemType: 'energy',
					id: type,
					position: 0,
				},
			],
		}),
	);
};
